/**
 * LevelSystem.ts - Character leveling and progression management
 * Handles experience gains, level ups, stat increases, spell learning
 */

import {
  FF1Class,
  STAT_GROWTH,
  BASE_STATS,
  getExpForLevel,
  calculateLevelUps,
  getStatIncreases,
  getSpellsLearnedAtLevel,
  canUpgradeClass,
} from './data/LevelData';
import { Character, CharacterClass, LevelUpResult } from './types/character';

// ============================================================================
// Level System
// ============================================================================

export class LevelSystem {
  /**
   * Add experience to a character and check for level ups
   * @param character - Character to gain experience
   * @param expGained - Amount of experience gained
   * @returns Array of level up results (can be multiple levels)
   */
  addExperience(character: Character, expGained: number): LevelUpResult[] {
    const levelUps: LevelUpResult[] = [];

    // Add experience
    character.experience += expGained;

    // Check for level ups
    while (character.level < 50) {
      const expRequired = getExpForLevel(character.level + 1);

      if (character.experience >= expRequired) {
        const levelUpResult = this.levelUp(character);
        levelUps.push(levelUpResult);
      } else {
        break; // No more level ups
      }
    }

    return levelUps;
  }

  /**
   * Level up a character
   * @param character - Character to level up
   * @returns Level up result with stat increases and spells learned
   */
  private levelUp(character: Character): LevelUpResult {
    const oldLevel = character.level;
    const newLevel = oldLevel + 1;

    // Increase level
    character.level = newLevel;

    // Calculate stat increases
    const statIncreases = getStatIncreases(
      character.class as unknown as FF1Class,
      oldLevel,
      newLevel
    );

    // Apply stat increases
    character.maxHP += statIncreases.hp;
    character.maxMP += statIncreases.mp;
    character.strength += statIncreases.strength;
    character.agility += statIncreases.agility;
    character.intelligence += statIncreases.intelligence;
    character.vitality += statIncreases.vitality;
    character.luck += statIncreases.luck;

    // Restore HP/MP to full on level up (FF1 behavior)
    character.currentHP = character.maxHP;
    character.currentMP = character.maxMP;

    // Learn new spells
    const spellsLearned = getSpellsLearnedAtLevel(
      character.class as unknown as FF1Class,
      newLevel
    );

    // Add spells to character's known spells
    spellsLearned.forEach((spell) => {
      if (!character.knownSpells.includes(spell.spellId)) {
        character.knownSpells.push(spell.spellId);
      }
    });

    // Update spell charges (gain charges at certain levels)
    this.updateSpellCharges(character);

    // Check for class upgrade availability
    const classUpgrade = canUpgradeClass(character.class as unknown as FF1Class, newLevel);
    let classUpgradeInfo = undefined;

    if (classUpgrade) {
      classUpgradeInfo = {
        toClassName: classUpgrade.toClassName,
        bonuses: this.formatClassUpgradeBonuses(classUpgrade),
      };
    }

    return {
      newLevel,
      statIncreases,
      spellsLearned,
      classUpgradeAvailable: classUpgradeInfo,
    };
  }

  /**
   * Update spell charges when leveling up
   * Characters gain additional spell charges at certain levels
   */
  private updateSpellCharges(character: Character): void {
    const classType = character.class as unknown as FF1Class;
    const level = character.level;

    // Gain spell charges every 4 levels for magic classes
    if (level % 4 === 0 && level <= 32) {
      const levelIndex = Math.floor(level / 4) - 1;

      // Ensure we have arrays initialized
      if (!character.maxSpellCharges) {
        character.maxSpellCharges = [0, 0, 0, 0, 0, 0, 0, 0];
      }
      if (!character.spellCharges) {
        character.spellCharges = [0, 0, 0, 0, 0, 0, 0, 0];
      }

      if (levelIndex >= 0 && levelIndex < 8) {
        // RedMage, WhiteMage, BlackMage gain charges
        if (
          classType === FF1Class.RedMage ||
          classType === FF1Class.WhiteMage ||
          classType === FF1Class.BlackMage
        ) {
          if (character.maxSpellCharges[levelIndex] !== undefined) {
            character.maxSpellCharges[levelIndex]++;
          }
          if (character.spellCharges[levelIndex] !== undefined) {
            character.spellCharges[levelIndex]++;
          }
        }
      }
    }
  }

  /**
   * Format class upgrade bonuses for display
   */
  private formatClassUpgradeBonuses(upgrade: any): string[] {
    const bonuses: string[] = [];

    Object.entries(upgrade.statsBonus).forEach(([stat, value]) => {
      if (value) {
        bonuses.push(`+${value} ${stat.toUpperCase()}`);
      }
    });

    if (upgrade.gainsSpellAccess) {
      if (upgrade.fromClass === FF1Class.Fighter) {
        bonuses.push('Gains White Magic Lv1-3');
      } else if (upgrade.fromClass === FF1Class.Thief) {
        bonuses.push('Gains Black Magic Lv1-4');
      }
    }

    return bonuses;
  }

  /**
   * Distribute experience to all living party members
   * @param party - Array of party members
   * @param totalExp - Total experience to distribute
   * @returns Map of character index to level up results
   */
  distributeExperience(
    party: Character[],
    totalExp: number
  ): Map<number, LevelUpResult[]> {
    const levelUpResults = new Map<number, LevelUpResult[]>();

    party.forEach((character, index) => {
      // Only living characters gain experience
      if (character.currentHP > 0) {
        const results = this.addExperience(character, totalExp);

        if (results.length > 0) {
          levelUpResults.set(index, results);
        }
      }
    });

    return levelUpResults;
  }

  /**
   * Initialize a character at level 1
   * @param classType - Character class
   * @param name - Character name (optional)
   * @returns New character
   */
  createCharacter(classType: FF1Class, name: string = ''): Character {
    const base = BASE_STATS[classType];

    return {
      class: classType as unknown as CharacterClass,
      name: name || this.getDefaultClassName(classType),
      level: 1,
      experience: 0,

      strength: base.strength,
      agility: base.agility,
      intelligence: base.intelligence,
      vitality: base.vitality,
      luck: base.luck,

      currentHP: base.hp,
      maxHP: base.hp,
      currentMP: base.mp,
      maxMP: base.mp,

      weapon: 0,
      armor: 0,
      helmet: 0,
      gloves: 0,
      accessory: 0,

      spellCharges: [0, 0, 0, 0, 0, 0, 0, 0],
      maxSpellCharges: [0, 0, 0, 0, 0, 0, 0, 0],
      knownSpells: [],

      status: 0, // StatusEffect.None
    };
  }

  /**
   * Get default class name for display
   */
  private getDefaultClassName(classType: FF1Class): string {
    const names: Record<FF1Class, string> = {
      [FF1Class.Fighter]: 'Fighter',
      [FF1Class.Thief]: 'Thief',
      [FF1Class.Monk]: 'Monk',
      [FF1Class.RedMage]: 'Red Mage',
      [FF1Class.WhiteMage]: 'White Mage',
      [FF1Class.BlackMage]: 'Black Mage',
    };
    return names[classType];
  }

  /**
   * Get experience progress as percentage (0-100)
   */
  getExpProgress(character: Character): number {
    if (character.level >= 50) return 100;

    const currentLevelExp = getExpForLevel(character.level);
    const nextLevelExp = getExpForLevel(character.level + 1);
    const expInLevel = character.experience - currentLevelExp;
    const expNeeded = nextLevelExp - currentLevelExp;

    return Math.round((expInLevel / expNeeded) * 100);
  }

  /**
   * Get experience needed for next level
   */
  getExpToNextLevel(character: Character): number {
    if (character.level >= 50) return 0;

    const nextLevelExp = getExpForLevel(character.level + 1);
    return Math.max(0, nextLevelExp - character.experience);
  }

  /**
   * Check if character is ready to level up
   */
  canLevelUp(character: Character): boolean {
    if (character.level >= 50) return false;

    const nextLevelExp = getExpForLevel(character.level + 1);
    return character.experience >= nextLevelExp;
  }

  /**
   * Calculate total number of level ups available
   */
  countAvailableLevelUps(character: Character): number {
    return calculateLevelUps(character.level, character.experience);
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const levelSystem = new LevelSystem();
